import { Button, Snackbar, Alert, IconButton } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";

const TestingTerminal = () => {
  const terminalRef = useRef(null);
  const terminalInstance = useRef(null);
  const wsRef = useRef(null);
  const fitAddonRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });


  const WS_URL = "ws://localhost:3006";

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    const styleId = 'terminal-custom-styles';
    let existingStyle = document.getElementById(styleId);
    
    if (!existingStyle) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .xterm {
          padding: 0 !important;
          height: 100% !important;
          width: 100% !important;
        }
        .xterm-viewport {
          background-color: #1e1e1e !important;
          overflow-y: scroll !important;
          scrollbar-width: thin;
          scrollbar-color: rgba(121, 121, 121, 0.4) transparent;
          width: 100% !important;
        }
        .xterm-viewport::-webkit-scrollbar {
          width: 10px;
        }
        .xterm-viewport::-webkit-scrollbar-track {
          background: transparent;
        }
        .xterm-viewport::-webkit-scrollbar-thumb {
          background-color: rgba(121, 121, 121, 0.4);
          border-radius: 10px;
          border: 2px solid transparent;
        }
        .xterm-viewport::-webkit-scrollbar-thumb:hover {
          background-color: rgba(100, 100, 100, 0.7);
        }
        .xterm-screen {
          margin: 0 !important;
          padding: 0 !important;
          display: block !important;
          width: 100% !important;
        }
        .xterm-rows {
          padding: 0 !important;
          margin: 0 !important;
        }
        .xterm-row {
          display: block !important;
          width: 100% !important;
        }
        .xterm .xterm-helpers {
          position: absolute;
          top: 0;
          left: 0;
          z-index: 5;
        }
      `;
      document.head.appendChild(style);
    }

    const terminal = new Terminal({
      cursorBlink: true,
      cursorStyle: "block",
      fontSize: 14,
      fontFamily: "'Cascadia Code', 'Consolas', 'Courier New', monospace",
      fontWeight: "normal",
      fontWeightBold: "bold",
      lineHeight: 1.2,
      letterSpacing: 0,
      scrollback: 10000,
      theme: {
        background: "#1e1e1e",
        foreground: "#cccccc",
        cursor: "#ffffff",
        cursorAccent: "#1e1e1e",
        selection: "rgba(255, 255, 255, 0.3)",
        black: "#000000",
        red: "#cd3131",
        green: "#0dbc79",
        yellow: "#e5e510",
        blue: "#2472c8",
        magenta: "#bc3fbc",
        cyan: "#11a8cd",
        white: "#e5e5e5",
        brightBlack: "#666666",
        brightRed: "#f14c4c",
        brightGreen: "#23d18b",
        brightYellow: "#f5f543",
        brightBlue: "#3b8eea",
        brightMagenta: "#d670d6",
        brightCyan: "#29b8db",
        brightWhite: "#ffffff",
      },
      allowProposedApi: true,
      allowTransparency: false,
      convertEol: false,
      disableStdin: false,
      windowsMode: false,
    });

    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    fitAddonRef.current = fitAddon;

    if (terminalRef.current) {
      terminal.open(terminalRef.current);
      
      setTimeout(() => {
        try {
          fitAddon.fit();
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(
              JSON.stringify({
                type: "resize",
                cols: terminal.cols,
                rows: terminal.rows,
              })
            );
          }
        } catch (e) {
        }
      }, 100);
    }

    terminal.focus();
    terminalInstance.current = terminal;

    const connectWebSocket = () => {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        showSnackbar("Connected to terminal server", "success");
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          switch (message.type) {
            case "connected":
              // Send terminal dimensions after connection
              if (terminal.cols && terminal.rows) {
                ws.send(
                  JSON.stringify({
                    type: "resize",
                    cols: terminal.cols,
                    rows: terminal.rows,
                  })
                );
              }
              break;

            case "output":
              if (message.data) {
                terminal.write(message.data);
              }
              break;

            case "error":
              showSnackbar(message.message, "error");
              terminal.write(`\r\n\x1b[31mError: ${message.message}\x1b[0m\r\n`);
              break;

            case "exit":
              showSnackbar("Terminal session ended", "warning");
              terminal.write("\r\n\x1b[33mTerminal session ended\x1b[0m\r\n");
              setIsConnected(false);
              break;

            case "pong":
              break;

            default:
          }
        } catch (error) {
        }
      };

      ws.onerror = (error) => {
        showSnackbar("Connection failed", "error");
        setIsConnected(false);
      };

      ws.onclose = () => {
        setIsConnected(false);
        showSnackbar("Disconnected from server", "warning");

        // Attempt to reconnect after 3 seconds
        setTimeout(() => {
          if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
            connectWebSocket();
          }
        }, 3000);
      };
    };

    connectWebSocket();

    terminal.onData((data) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({
            type: "input",
            data: data,
          })
        );
      }
    });

    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (fitAddonRef.current && terminalInstance.current) {
          try {
            fitAddonRef.current.fit();
            
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.send(
                JSON.stringify({
                  type: "resize",
                  cols: terminalInstance.current.cols,
                  rows: terminalInstance.current.rows,
                })
              );
            }
          } catch (e) {
          }
        }
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    const heartbeat = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearInterval(heartbeat);
      clearTimeout(resizeTimeout);
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (terminalInstance.current) {
        terminalInstance.current.dispose();
      }
    };
  }, []);

  const sendCommand = (command) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: "input",
          data: command + "\r",
        })
      );
    } else {
      showSnackbar("Not connected to terminal server", "error");
    }
  };

  const clearTerminal = () => {
    if (terminalInstance.current) {
      terminalInstance.current.clear();
    }
  };

  const reconnect = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    showSnackbar("Reconnecting...", "info");
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100vh",
        backgroundColor: "#1e1e1e",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#252526",
          borderBottom: "1px solid #3e3e42",
          padding: "8px 16px",
          height: "35px",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{
                fontSize: "13px",
                color: "#cccccc",
                fontWeight: "500",
                fontFamily: "system-ui, -apple-system, sans-serif",
              }}
            >
              Terminal
            </span>
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: isConnected ? "#4ec9b0" : "#f48771",
                boxShadow: isConnected
                  ? "0 0 4px rgba(78, 201, 176, 0.6)"
                  : "0 0 4px rgba(244, 135, 113, 0.6)",
              }}
            />
          </div>
          <span
            style={{
              fontSize: "11px",
              color: "#858585",
              fontFamily: "system-ui, -apple-system, sans-serif",
            }}
          >
            {isConnected ? "Connected" : "Disconnected"}
          </span>
        </div>

        <div style={{ display: "flex", gap: "4px" }}>
          <IconButton
            size="small"
            onClick={clearTerminal}
            title="Clear Terminal"
            sx={{
              color: "#cccccc",
              padding: "4px",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm3.854 9.854a.5.5 0 0 1-.708 0L8 7.707l-3.146 3.147a.5.5 0 0 1-.708-.708L7.293 7 4.146 3.854a.5.5 0 1 1 .708-.708L8 6.293l3.146-3.147a.5.5 0 0 1 .708.708L8.707 7l3.147 3.146a.5.5 0 0 1 0 .708z"/>
            </svg>
          </IconButton>
          {!isConnected && (
            <IconButton
              size="small"
              onClick={reconnect}
              title="Reconnect"
              sx={{
                color: "#cccccc",
                padding: "4px",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
              </svg>
            </IconButton>
          )}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          backgroundColor: "#1e1e1e",
          overflow: "hidden",
          position: "relative",
          display: "block",
        }}
      >
        <div
          ref={terminalRef}
          style={{
            position: "absolute",
            top: "8px",
            left: "8px",
            right: "8px",
            bottom: "8px",
            padding: 0,
            margin: 0,
            display: "block",
            textAlign: "left",
          }}
        />
      </div>

      <div
        style={{
          backgroundColor: "#252526",
          borderTop: "1px solid #3e3e42",
          padding: "8px 16px",
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
          height: "45px",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Button
          onClick={() => sendCommand("ls -la")}
          variant="text"
          disabled={!isConnected}
          size="small"
          sx={{
            color: "#cccccc",
            textTransform: "none",
            fontSize: "12px",
            padding: "4px 12px",
            minHeight: "28px",
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
            "&:disabled": {
              color: "#6e6e6e",
              backgroundColor: "transparent",
            },
          }}
        >
          ls -la
        </Button>
        <Button
          onClick={() => sendCommand("pwd")}
          variant="text"
          disabled={!isConnected}
          size="small"
          sx={{
            color: "#cccccc",
            textTransform: "none",
            fontSize: "12px",
            padding: "4px 12px",
            minHeight: "28px",
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
            "&:disabled": {
              color: "#6e6e6e",
              backgroundColor: "transparent",
            },
          }}
        >
          pwd
        </Button>
        <Button
          onClick={() => sendCommand("whoami")}
          variant="text"
          disabled={!isConnected}
          size="small"
          sx={{
            color: "#cccccc",
            textTransform: "none",
            fontSize: "12px",
            padding: "4px 12px",
            minHeight: "28px",
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
            "&:disabled": {
              color: "#6e6e6e",
              backgroundColor: "transparent",
            },
          }}
        >
          whoami
        </Button>
        <Button
          onClick={() => sendCommand("date")}
          variant="text"
          disabled={!isConnected}
          size="small"
          sx={{
            color: "#cccccc",
            textTransform: "none",
            fontSize: "12px",
            padding: "4px 12px",
            minHeight: "28px",
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
            "&:disabled": {
              color: "#6e6e6e",
              backgroundColor: "transparent",
            },
          }}
        >
          date
        </Button>
        <Button
          onClick={() => sendCommand("clear")}
          variant="text"
          disabled={!isConnected}
          size="small"
          sx={{
            color: "#cccccc",
            textTransform: "none",
            fontSize: "12px",
            padding: "4px 12px",
            minHeight: "28px",
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
            "&:disabled": {
              color: "#6e6e6e",
              backgroundColor: "transparent",
            },
          }}
        >
          clear
        </Button>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{ bottom: "60px !important" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            fontSize: "13px",
            "& .MuiAlert-icon": {
              fontSize: "18px",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TestingTerminal;